import React, { useCallback, useState } from "react";

const TICKS = [1, 2, 3, 4, 5];

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const round1 = (value) => Math.round(value * 10) / 10;

/**
 * ContinuousSlider: a 1.0–5.0 survey slider with a custom track and thumb over a
 * native range input, which provides dragging, touch and the slider semantics.
 *
 * Keyboard: Arrow keys move by `keyStep`, Page Up/Down by 1, Home/End jump to the ends.
 * Haptic feedback fires on whole-number crossings where the device supports it.
 *
 * @param {object} props
 * @param {number} [props.value=3]
 * @param {(value: number) => void} props.onChange
 * @param {number} [props.min=1]
 * @param {number} [props.max=5]
 * @param {number} [props.step=0.1] Pointer resolution.
 * @param {number} [props.keyStep=0.5] Arrow-key increment.
 * @param {string} [props.lowLabel="Low"]
 * @param {string} [props.highLabel="High"]
 * @param {string} [props["aria-labelledby"]] id of the question text.
 * @param {boolean} [props.disabled=false]
 */
const ContinuousSlider = ({
  value = 3,
  onChange,
  min = 1,
  max = 5,
  step = 0.1,
  keyStep = 0.5,
  lowLabel = "Low",
  highLabel = "High",
  disabled = false,
  "aria-labelledby": labelledBy,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const percentage = ((value - min) / (max - min)) * 100;

  const triggerHaptic = useCallback(() => {
    if (navigator.vibrate) navigator.vibrate(10);
  }, []);

  const commit = (next) => {
    const newValue = round1(clamp(next, min, max));
    if (newValue === value) return;
    onChange(newValue);
    if (Math.floor(newValue) !== Math.floor(value)) triggerHaptic();
  };

  const handleKeyDown = (e) => {
    const moves = {
      ArrowRight: value + keyStep,
      ArrowUp: value + keyStep,
      ArrowLeft: value - keyStep,
      ArrowDown: value - keyStep,
      PageUp: value + 1,
      PageDown: value - 1,
      Home: min,
      End: max,
    };
    if (!(e.key in moves)) return;
    e.preventDefault();
    commit(moves[e.key]);
  };

  const startDrag = () => {
    setIsDragging(true);
    triggerHaptic();
  };

  return (
    <div className="w-full select-none" style={{ touchAction: "none" }}>
      <div className="relative py-2">
        <div className="relative h-3 overflow-hidden rounded-full bg-line">
          <div
            className="absolute inset-y-0 left-0 rounded-full bg-accent"
            style={{ width: `${percentage}%` }}
          />
          <div className="absolute inset-0 flex items-center justify-between px-1.5" aria-hidden="true">
            {TICKS.map((mark) => (
              <span
                key={mark}
                className={`h-1.5 w-1.5 rounded-full ${
                  value >= mark ? "bg-white/60" : "bg-fg-muted/50"
                }`}
              />
            ))}
          </div>
        </div>

        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => commit(parseFloat(e.target.value))}
          onKeyDown={handleKeyDown}
          onMouseDown={startDrag}
          onMouseUp={() => setIsDragging(false)}
          onTouchStart={startDrag}
          onTouchEnd={() => setIsDragging(false)}
          disabled={disabled}
          aria-labelledby={labelledBy}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={value}
          aria-valuetext={`${value.toFixed(1)} of ${max}, from ${lowLabel} to ${highLabel}`}
          className="peer absolute inset-x-0 -top-2 z-10 h-12 w-full cursor-pointer appearance-none opacity-0 disabled:cursor-not-allowed"
        />

        <span
          aria-hidden="true"
          className={`pointer-events-none absolute top-1/2 h-7 w-7 -translate-x-1/2 -translate-y-1/2 rounded-full border-4 border-white bg-accent shadow-card transition-transform duration-150 peer-focus-visible:ring-2 peer-focus-visible:ring-focus peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-surface ${
            isDragging ? "scale-125" : "scale-100"
          }`}
          style={{ left: `${percentage}%` }}
        />
      </div>

      <div className="mt-2 flex justify-between text-sm font-medium text-fg-muted">
        <span>{lowLabel}</span>
        <span className="text-right">{highLabel}</span>
      </div>
      <div className="mt-3 flex justify-between px-1 text-xs text-fg-muted" aria-hidden="true">
        {TICKS.map((mark) => (
          <span key={mark}>{mark}</span>
        ))}
      </div>
    </div>
  );
};

export default ContinuousSlider;
